import {
    BuiBreadcrumb,
    type IBuiBreadcrumbItemProps,
    type IBuiBreadcrumbProps,
} from "@btnet/blueprint-components";
import { cn } from "@btnet/utilities-core";

import "./BTBreadcrumb.less";

import { useBTBreadcrumb } from "./useBTBreadcrumb";

/**
 * Props for the BTBreadcrumb component.
 *
 * A curated subset of BuiBreadcrumbProps with responsive collapse support.
 * Only exposes the props needed for breadcrumb navigation in BTNet.
 */
export type IBTBreadcrumbProps = Pick<
    IBuiBreadcrumbProps,
    "testid" | "children" | "separator" | "className"
> & {
    /** Optional HTML id for the nav element. */
    id?: string;
    /** Accessible label for the nav element. Defaults to "Breadcrumb". */
    "aria-label"?: string;
};

/**
 * BTBreadcrumb.Item props - a curated subset of BuiBreadcrumbItemProps.
 * Only exposes the props needed for breadcrumb items in BTNet.
 */
export type IBTBreadcrumbItemProps = Pick<
    IBuiBreadcrumbItemProps,
    "testid" | "children" | "className" | "href" | "onClick" | "overlay" | "separator"
>;

const BTBreadcrumbItem = ({
    testid,
    children,
    className,
    href,
    onClick,
    overlay,
    separator,
}: IBTBreadcrumbItemProps) => (
    <BuiBreadcrumb.Item
        testid={testid}
        className={cn("bt-breadcrumb-item", className)}
        // Omit href/onClick when undefined so the key doesn't exist in props.
        // Ant Design checks `'href' in props` and renders <a> when the key is present, even if the value is undefined.
        {...(href !== undefined && { href })}
        {...(onClick !== undefined && { onClick })}
        overlay={overlay}
        separator={separator}
    >
        {children}
    </BuiBreadcrumb.Item>
);

const BTBreadcrumbComponent = (props: IBTBreadcrumbProps) => {
    const { buiProps, navProps, processedChildren } = useBTBreadcrumb(props);

    return (
        <nav {...navProps}>
            <BuiBreadcrumb {...buiProps}>{processedChildren}</BuiBreadcrumb>
        </nav>
    );
};

/**
 * BTBreadcrumb component - a styled breadcrumb navigation with responsive collapse.
 * Wraps BuiBreadcrumb from @buildertrend/components with a `<nav>` element for accessibility
 * and built-in responsive collapse behavior on mobile viewports.
 *
 * Accessibility is built-in:
 * - Wrapped in `<nav aria-label="Breadcrumb">` by default; override with the `aria-label` prop when the page has multiple breadcrumb navs.
 * - For the current page, add `aria-current="page"` to the element
 *
 * Responsive collapse (enabled by default):
 * - On mobile (max-width: 768px), middle items collapse into a dropdown menu
 * - First and last items remain visible
 * - Collapsed items are accessible via a "..." button dropdown
 * @param props - The component props.
 * @param props.testid - Test ID for the breadcrumb.
 * @param props.children - BTBreadcrumb.Item children.
 * @param props.separator - Separator between items. Defaults to "/".
 * @param props.className - Additional CSS class names.
 * @param props.id - Optional HTML id for the nav element.
 * @example
 * ```tsx
 * <BTBreadcrumb testid="nav-breadcrumb">
 *   <BTBreadcrumb.Item testid="home">
 *     <a href="/">Home</a>
 *   </BTBreadcrumb.Item>
 *   <BTBreadcrumb.Item testid="products">
 *     <a href="/products">Products</a>
 *   </BTBreadcrumb.Item>
 *   <BTBreadcrumb.Item testid="current">
 *     <span aria-current="page">Current Page</span>
 *   </BTBreadcrumb.Item>
 * </BTBreadcrumb>
 * ```
 */
export const BTBreadcrumb = Object.assign(BTBreadcrumbComponent, {
    /**
     * BTBreadcrumb.Item sub-component for individual breadcrumb items.
     * Wraps BuiBreadcrumb.Item from @buildertrend/components.
     * @param props - The component props.
     * @param props.testid - Test ID for the breadcrumb item.
     * @param props.children - The content to render inside the breadcrumb item.
     * @param props.className - Additional CSS class names.
     * @param props.href - Link URL for the breadcrumb item.
     * @param props.onClick - Click handler for the breadcrumb item.
     */
    Item: BTBreadcrumbItem,
});
